import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getClassName } from "@utils";
import { PADDING_OFFSET } from "./constants";
import {
  getCurrentPosition,
  getPositionOffsetStyles,
  getTooltipContentPosition,
  isTopOrBottomPosition,
} from "./utils";
import { TooltipPosition, TooltipSize } from "./TooltipContent.types";
import { ArrowIcon } from "./icons";
import css from "./TooltipContent.module.scss";

export interface TooltipContentProps {
  isVisible: boolean;
  parentRef: React.RefObject<HTMLDivElement>;
  arrow?: boolean;
  autoPosition?: boolean;
  dark?: boolean;
  className?: string;
  content?: React.ReactNode;
  position?: TooltipPosition;
  positionOffset?: number;
  positionOverride?: {
    left?: number;
    top?: number;
  };
  size?: TooltipSize;
  title?: string;
  text?: string;
  width?: number;
}

const TooltipContent: React.FC<TooltipContentProps> = ({
  isVisible: isVisibleProp,
  parentRef,
  arrow = true,
  autoPosition = false,
  dark = false,
  className,
  content,
  position: positionProp = TooltipPosition.Top,
  positionOffset = PADDING_OFFSET,
  positionOverride,
  size = TooltipSize.M,
  title,
  text,
  width,
}) => {
  const [isVisible, setIsVisible] = useState(isVisibleProp);
  const [position, setPosition] = useState(positionProp);

  const tooltipRef = useRef<HTMLDivElement>(null);

  const attrs = {
    tooltip: {
      ref: tooltipRef,
      role: "tooltip",
      className: getClassName(
        css.tooltip,
        css[`tooltip${position}`],
        css[`tooltip${size}`],
        dark && css.tooltipDark,
        !isVisibleProp && css.tooltipClosing,
        className
      ),
      onAnimationEnd: () => {
        if (!isVisibleProp) {
          setIsVisible(false);
          setPosition(positionProp);
        }
      },
      style: {
        ...(typeof width === "number"
          ? {
              width: isTopOrBottomPosition(position) ? width : width + PADDING_OFFSET,
            }
          : {}),
        ...getPositionOffsetStyles(position, positionOffset),
      },
    },
    content: {
      className: css.content,
    },
    title: {
      className: css.title,
    },
    text: {
      className: css.text,
    },
    arrowIcon: {
      dark,
      position,
      className: css.arrowIcon,
    },
  };

  const handleTooltipPosition = useCallback(() => {
    const tooltip = tooltipRef.current;
    const parent = parentRef.current;

    if (isVisible && tooltip && parent) {
      const transformStyle = getTooltipContentPosition(
        tooltip,
        parent,
        position,
        size,
        positionOverride
      );

      tooltip.style.transform = transformStyle;
    }
  }, [parentRef, isVisible, position, size]);

  const renderContent = () => {
    if (content) {
      return content;
    }

    return (
      <>
        {title ? <div {...attrs.title}>{title}</div> : null}
        {text ? <div {...attrs.text}>{text}</div> : null}
      </>
    );
  };

  useEffect(() => {
    if (isVisibleProp) {
      setIsVisible(true);
    }
  }, [isVisibleProp]);

  useEffect(() => {
    const tooltip = tooltipRef.current;

    if (isVisible && tooltip) {
      window.requestAnimationFrame(() => {
        setPosition(getCurrentPosition(tooltip, positionProp, autoPosition));
      });
    }
  }, [autoPosition, isVisible, positionProp]);

  useEffect(() => {
    handleTooltipPosition();
  }, [handleTooltipPosition]);

  useEffect(() => {
    const handler = () => {
      window.requestAnimationFrame(handleTooltipPosition);
    };

    document.addEventListener("scroll", handler, true);

    return () => {
      document.removeEventListener("scroll", handler, true);
    };
  }, [handleTooltipPosition]);

  if (!isVisible) {
    return null;
  }

  return createPortal(
    <div {...attrs.tooltip}>
      <div {...attrs.content}>
        {renderContent()}
        {arrow ? <ArrowIcon {...attrs.arrowIcon} /> : null}
      </div>
    </div>,
    document.body
  );
};

export default TooltipContent;
